import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { postsApi } from '../services/api';
import hiveService from '../services/hive';
import { usePostCache } from '../context/PostCacheContext';
import { useSettings } from '../context/SettingsContext';
import PostCard from '../components/PostCard/PostCard';
import InlineCommentNode from '../components/InlineCommentNode/InlineCommentNode';
import LoadingSpinner from '../components/LoadingSpinner/LoadingSpinner';
import SidebarHeader from '../components/SidebarHeader/SidebarHeader';
import { FEED_AUTHORS } from '../config/feed-details';
import { buildContentPath, buildFlexDetailPath } from '../utils/postUrls';
import { buildCommentTree, flattenCommentTree } from '../utils/commentTree';
import useAuthorAvatar from '../utils/useAuthorAvatar';
import './FlexDetail.css';

const normalizeNestedComments = (nodes = [], rootAuthor, rootPermlink, seen = new Set()) => {
  if (!Array.isArray(nodes)) return [];

  const out = [];
  for (const node of nodes) {
    const nodeAuthor = node?.author || '';
    const nodePermlink = node?.permlink || '';
    if (!nodeAuthor || !nodePermlink) continue;

    const key = `${nodeAuthor}/${nodePermlink}`;
    if (key === `${rootAuthor}/${rootPermlink}`) continue;
    if (seen.has(key)) continue;
    seen.add(key);

    out.push({
      ...node,
      replies: normalizeNestedComments(node?.replies || [], rootAuthor, rootPermlink, seen)
    });
  }

  return out;
};

const FlexDetail = ({ onOpenLeftSidebar, onOpenRightSidebar }) => {
  const { author: rawAuthor, permlink } = useParams();
  const author = rawAuthor?.startsWith('@') ? rawAuthor.slice(1) : rawAuthor;
  const navigate = useNavigate();
  const { getFromCache, addToCache } = usePostCache();
  const [post, setPost] = useState(null);
  const [parentPost, setParentPost] = useState(null);
  const [comments, setComments] = useState([]); // nested tree
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [commentsStatus, setCommentsStatus] = useState('idle'); // idle | loading | retrying | done | none
  const [expandedComments, setExpandedComments] = useState(new Set());
  const fetchingRef = useRef(false);

  const returnPath = sessionStorage.getItem('returnPath') || '/';
  const returnPathIsDetail = /\/post\/|\/flexdetail/.test(returnPath);
  const backLink = returnPathIsDetail ? '/feed/flex' : returnPath;
  const backLabel = returnPathIsDetail ? '← To home' : '← Back to feed';

  const handleBackClick = (e) => {
    e.preventDefault();
    // Signal feed to preserve previous scroll and focus this post
    sessionStorage.setItem('preserveScroll', 'true');
    sessionStorage.setItem('focusPost', `${author}/${permlink}`);
    navigate(backLink);
  };

  // Use refs for cache functions so fetchAll can stay stable and avoid re-run loops
  const getFromCacheRef = useRef(getFromCache);
  const addToCacheRef = useRef(addToCache);
  useEffect(() => { getFromCacheRef.current = getFromCache; addToCacheRef.current = addToCache; }, [getFromCache, addToCache]);

  const mountedRef = useRef(false);

  const loadParentPreview = useCallback(async (currentPost) => {
    const pAuth = currentPost?.parent_author || currentPost?.parentAuthor;
    const pPerm = currentPost?.parent_permlink || currentPost?.parentPermlink;
    const normalizedParentPermlink = String(pPerm || '').trim().toLowerCase();
    const isPlaceholderPermlink = normalizedParentPermlink === 'none' || normalizedParentPermlink === 'null';

    if (!pAuth || !pPerm || isPlaceholderPermlink) {
      setParentPost(null);
      return;
    }

    const isContainerParent = FEED_AUTHORS.includes(String(pAuth).toLowerCase());
    if (isContainerParent) {
      setParentPost(null);
      return;
    }

    const cachedParent = getFromCacheRef.current(pAuth, pPerm);
    if (cachedParent?.post) {
      setParentPost(cachedParent.post);
      return;
    }

    try {
      const parentData = await postsApi.getPost(pAuth, pPerm, false);
      if (!mountedRef.current) return;
      if (parentData?.post) {
        setParentPost(parentData.post);
        addToCacheRef.current(pAuth, pPerm, { post: parentData.post, comments: [], commentsLoaded: false });
      }
    } catch (_err) {
      if (!mountedRef.current) return;
      setParentPost(null);
    }
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      let hydratedFromCache = false;

      // Try cache first (flat comments stored under comments)
      const cached = getFromCacheRef.current(author, permlink);
      if (cached?.post) {
        setPost(cached.post);
        loadParentPreview(cached.post);
        hydratedFromCache = true;
        setLoading(false);
        if (cached.commentsLoaded && Array.isArray(cached.comments)) {
          // If cache has nested comments (legacy), detect and normalize
          const nestedFromCache = cached.nestedComments || cached.commentsNested || null;
          if (nestedFromCache && Array.isArray(nestedFromCache)) {
            const safeNested = normalizeNestedComments(nestedFromCache, author, permlink);
            setComments(safeNested);
            setCommentsStatus(safeNested.length > 0 ? 'done' : 'none');
          } else {
            const roots = buildCommentTree(cached.comments || [], author, permlink);
            const safeRoots = normalizeNestedComments(roots, author, permlink);
            setComments(safeRoots);
            setCommentsStatus(safeRoots.length > 0 ? 'done' : 'none');
          }
        }
      }

      // Fetch post first (fast)
      const postData = await postsApi.getPost(author, permlink, false);
      if (!mountedRef.current) return;
      setPost(postData.post);
      loadParentPreview(postData.post);
      addToCacheRef.current(author, permlink, { post: postData.post, comments: [], commentsLoaded: false });
      if (!hydratedFromCache) {
        setLoading(false);
      }

      // Avoid overlapping fetches
      if (fetchingRef.current) return;
      fetchingRef.current = true;

      // Fetch all comments (nested) from new endpoint
      setCommentsStatus('loading');
      let data = await postsApi.getPostAll(author, permlink);
      if (!mountedRef.current) { fetchingRef.current = false; return; }
      let nested = normalizeNestedComments(data.comments || [], author, permlink);
      const expectedReplies = Number(postData?.post?.replies || postData?.post?.children || 0);

      // If server returned no comments yet, retry with exponential backoff (covers timing/race conditions)
      if (expectedReplies > 0 && (!nested || nested.length === 0)) {
        setCommentsStatus('retrying');
        const delays = [300, 800, 1600, 3200, 6400];
        for (let i = 0; i < delays.length && (!nested || nested.length === 0); i++) {
          await new Promise(r => setTimeout(r, delays[i]));
          if (!mountedRef.current) break;
          try {
            data = await postsApi.getPostAll(author, permlink);
            nested = normalizeNestedComments(data.comments || [], author, permlink);
            // update status with attempt info
            setCommentsStatus(`retrying (${i + 1}/${delays.length})`);
          } catch (err) {
            // ignore and continue retrying
            setCommentsStatus(`retrying (${i + 1}/${delays.length})`);
          }
        }
      }

      if (nested && nested.length > 0) {
        setCommentsStatus('done');
      } else {
        setCommentsStatus('none');
      }

      // Build flat list for cache compatibility
      const flat = flattenCommentTree(nested);

      setComments(nested);
      addToCacheRef.current(author, permlink, { post: postData.post, comments: flat, nestedComments: nested, commentsLoaded: true });
    } catch (err) {
      if (!mountedRef.current) return;
      console.error('FlexDetail fetch error:', err);
      setError(`Failed to load post: ${err.response?.data?.error || err.message}`);
    } finally {
      fetchingRef.current = false;
      if (mountedRef.current) setLoading(false);
    }
  }, [author, permlink, loadParentPreview]);

  useEffect(() => {
    mountedRef.current = true;
    fetchAll();
    return () => { mountedRef.current = false; };
  }, [fetchAll]);

  const updateOptimisticComment = useCallback((targetPermlink, updates) => {
    const apply = (nodes = []) => nodes.map((node) => {
      const nextNode = { ...node };
      if (nextNode.permlink === targetPermlink) {
        Object.assign(nextNode, updates);
      }
      if (Array.isArray(nextNode.replies) && nextNode.replies.length > 0) {
        nextNode.replies = apply(nextNode.replies);
      }
      return nextNode;
    });

    setComments((prev) => apply(prev));
  }, []);

  const scheduleCommentConfirmationCheck = useCallback((commentAuthor, commentPermlink) => {
    const checkDelays = [20000, 60000];

    checkDelays.forEach((delay, index) => {
      setTimeout(async () => {
        const exists = await hiveService.checkPostExists(commentAuthor, commentPermlink);
        if (exists) {
          updateOptimisticComment(commentPermlink, { optimistic: false, optimisticStatus: 'confirmed' });
          return;
        }
        if (index === checkDelays.length - 1) {
          updateOptimisticComment(commentPermlink, { optimisticStatus: 'failed', optimisticError: 'Not found on-chain yet. Retry?' });
        }
      }, delay);
    });
  }, [updateOptimisticComment]);

  const handleOptimisticComment = useCallback((optimisticComment) => {
    setComments((prev) => [optimisticComment, ...prev]);
    if (post && typeof post.children === 'number') {
      setPost(p => ({ ...p, children: p.children + 1 }));
    }
    scheduleCommentConfirmationCheck(optimisticComment.author, optimisticComment.permlink);
  }, [scheduleCommentConfirmationCheck, post]);

  // Inserts a reply into the correct position in the nested tree.
  // Used by InlineCommentNode so the comment lives in durable page state.
  const handleOptimisticReply = useCallback((parentAuthor, parentPermlink, optimisticComment) => {
    const insert = (nodes) => nodes.map((node) => {
      if (node.author === parentAuthor && node.permlink === parentPermlink) {
        return { ...node, replies: [optimisticComment, ...(node.replies || [])] };
      }
      if (Array.isArray(node.replies) && node.replies.length > 0) {
        return { ...node, replies: insert(node.replies) };
      }
      return node;
    });
    setComments((prev) => insert(prev));
    setExpandedComments((prev) => new Set([...prev, `${parentAuthor}/${parentPermlink}`]));
    if (post && typeof post.children === 'number') {
      setPost(p => ({ ...p, children: p.children + 1 }));
    }
    scheduleCommentConfirmationCheck(optimisticComment.author, optimisticComment.permlink);
  }, [scheduleCommentConfirmationCheck, post]);

  useEffect(() => {
    const scan = (nodes) => {
      nodes.forEach((comment) => {
        if (comment?.optimistic && comment?.optimisticStatus === 'pending') {
          scheduleCommentConfirmationCheck(comment.author, comment.permlink);
        }
        if (Array.isArray(comment.replies) && comment.replies.length > 0) {
          scan(comment.replies);
        }
      });
    };
    scan(comments);
  }, [comments, scheduleCommentConfirmationCheck]);

  const handleCommentPosted = () => {
    fetchAll();
  };

  const handleRetryOptimisticComment = async (comment) => {
    if (!comment.parentAuthor || !comment.parentPermlink) return;
    try {
      // Try to post via hiveService
      await hiveService.createComment(
        comment.author,
        comment.parentAuthor,
        comment.parentPermlink,
        comment.body,
        comment.rootAuthor || comment.parentAuthor,
        { permlink: comment.permlink }
      );
    } catch (err) {
      console.error('Retry failed', err);
    }
    fetchAll();
  };

  const { commentDisplayMode } = useSettings();
  const REPLY_DEPTH_LIMIT = parseInt(process.env.REACT_APP_REPLY_DEPTH_LIMIT, 10) || 4;
  const parentAuthor = post?.parentAuthor || post?.parent_author;
  const parentPermlink = post?.parentPermlink || post?.parent_permlink;
  const hasParent = Boolean(parentAuthor && parentPermlink);
  const parentIsContainerPost = Boolean(parentAuthor && FEED_AUTHORS.includes(String(parentAuthor).toLowerCase()));
  const showParentLink = hasParent && !parentIsContainerPost;
  const parentFlexPath = hasParent
    ? `/post/flexdetail/${parentAuthor}/${parentPermlink}`
    : null;
  const parentTargetPath = showParentLink
    ? (parentPost
      ? buildContentPath(parentAuthor, parentPermlink, parentPost)
      : parentFlexPath)
    : null;
  const parentPreviewAuthor = parentPost?.author || parentAuthor || null;
  const { avatarSources: parentAvatarSources, handleAvatarError: handleParentAvatarError } = useAuthorAvatar({
    author: parentPreviewAuthor,
    profileImage: parentPost?.profileImage || null
  });

  const parentPreviewText = useMemo(() => {
    if (!parentPost?.body) return 'Loading parent preview...';
    const clean = parentPost.body.replace(/!\[.*?\]\(.*?\)/g, '').replace(/<[^>]+>/g, '').trim();
    if (!clean) return 'Parent post has no text content.';
    const match = clean.match(/^(.{0,220}\S*)/s);
    const preview = match ? match[1] : clean;
    return preview.length < clean.length ? `${preview}...` : preview;
  }, [parentPost]);

  const renderCommentNode = (comment, level = 0, parentExpanded = false) => {
    if (commentDisplayMode === 'inline') {
      // InlineCommentNode handles replies internally
      return (
        <InlineCommentNode
          key={`${comment.author}-${comment.permlink}`}
          comment={comment}
          depth={level}
          maxDepth={REPLY_DEPTH_LIMIT}
          postAuthor={author}
          postPermlink={permlink}
          isDetailView={true}
          onOptimisticReply={handleOptimisticReply}
          onOptimisticCommentUpdate={updateOptimisticComment}
        />
      );
    }

    // Card view (default)
    const key = `${comment.author}/${comment.permlink}`;
    const commentExpanded = expandedComments.has(key);
    const hasReplies = comment.replies && comment.replies.length > 0;
    const isAtLimit = level >= REPLY_DEPTH_LIMIT;
    const isInExtra = parentExpanded;
    const isAtExtraLimit = parentExpanded && level >= REPLY_DEPTH_LIMIT + 2;

    const handleToggle = () => {
      if (commentExpanded) {
        setExpandedComments(prev => { const s = new Set(prev); s.delete(key); return s; });
      } else {
        setExpandedComments(prev => new Set([...prev, key]));
      }
    };

    const showChildren = hasReplies && (
      commentExpanded ||
      (isInExtra && !isAtExtraLimit)
    );

    return (
      <div key={key} className={`flex-comment-node level level-${level}`}>
        <PostCard
          post={comment}
          showActions={true}
          isDetailView={false}
          showCommentToggle={false}
          hideCommentButton={false}
          onExpandDeeper={hasReplies ? handleToggle : undefined}
          mobileLimitPath={isAtLimit && !isInExtra && !commentExpanded && hasReplies ? buildFlexDetailPath(comment.author, comment.permlink) : undefined}
          onCommentPosted={handleCommentPosted}
          onOptimisticComment={(optimisticComment) => handleOptimisticReply(comment.author, comment.permlink, optimisticComment)}
          onOptimisticCommentUpdate={updateOptimisticComment}
          onRetryOptimistic={handleRetryOptimisticComment}
        />
        {showChildren && (
          <div className="nested-replies">
            {comment.replies.map(r => renderCommentNode(r, level + 1, isInExtra || (isAtLimit && commentExpanded)))}
          </div>
        )}
        {isAtLimit && !isInExtra && !commentExpanded && hasReplies && (
          <Link to={buildFlexDetailPath(comment.author, comment.permlink)} className="view-deeper-link show-on-mobile-only">
            Show deeper replies →
          </Link>
        )}
        {isAtExtraLimit && hasReplies && (
          <Link to={buildFlexDetailPath(comment.author, comment.permlink)} className="view-deeper-link">
            Show deeper replies →
          </Link>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="post-detail-container">
        <SidebarHeader title="Flex Detail" onOpenLeftSidebar={onOpenLeftSidebar} onOpenRightSidebar={onOpenRightSidebar} />
        <LoadingSpinner text="Loading post..." />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="error-box">
        <SidebarHeader title="Flex Detail" onOpenLeftSidebar={onOpenLeftSidebar} onOpenRightSidebar={onOpenRightSidebar} />
        <p>{error || 'Post not found'}</p>
        <Link to={backLink} onClick={handleBackClick} className="back-link">{backLabel}</Link>
      </div>
    );
  }

  return (
    <div className="post-detail-container">
      <SidebarHeader title="Flex Detail" onOpenLeftSidebar={onOpenLeftSidebar} onOpenRightSidebar={onOpenRightSidebar} />
      <div className="detail-links-row">
        <Link to={backLink} onClick={handleBackClick} className="back-link">{backLabel}</Link>
        {parentTargetPath && (
          <Link to={parentTargetPath} className="back-link back-link-right">↑ View parent</Link>
        )}
      </div>

      {parentTargetPath && (
        <Link to={parentTargetPath} className="flex-parent-preview" aria-label={`View parent post by ${parentAuthor}`}>
          <div className="flex-parent-preview-head">
            <img
              src={parentAvatarSources[0] || ''}
              alt={parentPreviewAuthor || parentAuthor || 'Parent author'}
              className="flex-parent-preview-avatar"
              data-avatar-fallback-index="0"
              onError={handleParentAvatarError}
            />
            <div className="flex-parent-preview-head-copy">
              <div className="flex-parent-preview-kicker">Replying to</div>
              <div className="flex-parent-preview-title">@{parentAuthor}/{parentPermlink}</div>
            </div>
          </div>
          <div className={`flex-parent-preview-body${parentPost ? '' : ' is-loading'}`}>
            {parentPreviewText}
          </div>
        </Link>
      )}

      <PostCard
        post={post}
        showActions={true}
        onCommentPosted={handleCommentPosted}
        onOptimisticComment={handleOptimisticComment}
        onOptimisticCommentUpdate={updateOptimisticComment}
        isDetailView={true}
        enableContiguousImageGrid={true}
        showCommentToggle={false}
        hideCommentButton={true}
      />

      <div className="comments-section">
        <h3 className="comments-title">Comments</h3>

        {comments.length === 0 ? (
          <div className="empty-state">
            {commentsStatus === 'loading' || commentsStatus.startsWith('retrying') ? (
              <p>Fetching comments... {commentsStatus.startsWith('retrying') ? commentsStatus : ''}</p>
            ) : commentsStatus === 'none' ? (
              <p>No comments yet. Try reloading or check back in a moment.</p>
            ) : (
              <p>No comments yet. Be the first to comment!</p>
            )}
          </div>
        ) : (
          <div className="flex-comments-list">
            {comments.map(c => renderCommentNode(c))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FlexDetail;
